import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CardArt } from '../components/CardArt.tsx';
import { TopBar } from '../components/Chrome.tsx';
import { Icon } from '../components/Icon.tsx';
import { Delta, Score } from '../components/Indicators.tsx';
import { PriceChart } from '../components/PriceChart.tsx';
import { BuyModal, SellModal } from '../components/TradeModals.tsx';
import { cardDetail, fx as fetchFx, listHoldings } from '../lib/api.ts';
import {
  FX_FALLBACK,
  fmtHKD,
  fmtJpyAsHkd,
  fmtJpyAsHkdK,
  fmtPctSigned,
  fmtSignedHKD,
  fmtSpreadPct,
  fmtUSD,
  labelForSignal,
  usdToHkdDisplay,
} from '../lib/format.ts';
import type { CardArb, FxRate, SubName } from '../lib/types.ts';

const SUB_LABELS: Record<SubName, string> = {
  trend: 'Trend',
  velocity: 'Velocity',
  spread: 'Spread',
  'z-score': 'Z-score',
  pop: 'Pop growth',
};

const SUBS_ORDER: SubName[] = ['trend', 'velocity', 'spread', 'z-score', 'pop'];

type Range = '1D' | '1W' | '1M' | '3M' | '1Y' | 'ALL';

export function Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const detailQ = useQuery({
    queryKey: ['card', id],
    queryFn: () => cardDetail(id as string),
    enabled: Boolean(id),
  });
  const fxQ = useQuery({ queryKey: ['fx'], queryFn: fetchFx });
  const [range, setRange] = useState<Range>('1M');
  const [showBuy, setShowBuy] = useState(false);
  const [showSell, setShowSell] = useState(false);
  // Pull the user's open holdings for this card so Sell can target a specific id
  // (the position block aggregates across multiple buys; we sell the oldest).
  const openHoldingsQ = useQuery({
    queryKey: ['holdings', 'real', 'open'],
    queryFn: () => listHoldings('open'),
    enabled: Boolean(id),
  });

  // Esc to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') navigate('/', { replace: true });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  if (detailQ.isError) {
    return (
      <div className="screen screen-desktop">
        <TopBar view="detail" />
        <div style={{ padding: 40, color: 'var(--sell)' }}>Could not load card.</div>
      </div>
    );
  }
  if (!detailQ.data) {
    return (
      <div className="screen screen-desktop">
        <TopBar view="detail" />
        <div style={{ padding: 40, color: 'var(--ink-dim)' }}>loading…</div>
      </div>
    );
  }
  const card = detailQ.data;
  const fx = fxQ.data ?? FX_FALLBACK;
  const hkd = card.price === null ? null : usdToHkdDisplay(card.price, fx);
  const sigTone = card.signal === 'BUY' ? 'buy' : card.signal === 'SELL' ? 'sell' : 'hold';
  const fired = new Set(card.subs);

  return (
    <div className="screen screen-desktop">
      <TopBar view="detail" />
      <div className="scrim" onClick={() => navigate('/', { replace: true })} role="presentation" />
      <div className="modal" role="dialog" aria-label={`${card.jpEn ?? card.jp} detail`}>
        {/* LEFT column */}
        <div className="modal-left">
          <button
            type="button"
            className="modal-back"
            onClick={() => navigate(-1)}
            aria-label="Back"
          >
            ← Back
          </button>
          <div className="modal-art">
            <CardArt card={card} />
          </div>
          <div className="modal-meta">
            <div className={card.jpEn ? 'modal-name-jp' : 'modal-name-jp jp'}>
              {card.jpEn ?? card.jp}
            </div>
            <div className="modal-tagrow">
              <span className="tag">
                {card.set} · {card.num}
              </span>
              <span className="tag">{card.rarity}</span>
              {card.pop !== null && (
                <span className="tag">PSA 10 pop {card.pop.toLocaleString()}</span>
              )}
            </div>
            {card.snkrdunkUrl && (
              <div style={{ marginTop: 10 }}>
                <a
                  className="chip"
                  href={card.snkrdunkUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Open on Snkrdunk ↗
                </a>
              </div>
            )}
          </div>
          <ArbPanel arb={card.arb ?? null} snkrdunkUrl={card.snkrdunkUrl} fx={fx} />
          {card.arb && <DropshipPanel arb={card.arb} fx={fx} />}
        </div>

        {/* RIGHT column */}
        <div className="modal-right">
          <div className="modal-rhead">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div className="modal-bigprice">
                <span className="ccy">HKD</span>
                {hkd === null ? '—' : fmtHKD(hkd)}
                {card.price !== null && (
                  <span className="modal-hkd">≈ US${fmtUSD(card.price)}</span>
                )}
              </div>
              <div className="modal-bigdelta">
                <span>
                  <span style={{ color: 'var(--ink-mute)' }}>24h </span>
                  <Delta v={card.d1} size={12} />
                </span>
                <span>
                  <span style={{ color: 'var(--ink-mute)' }}>7d </span>
                  <Delta v={card.d7} size={12} />
                </span>
                <span>
                  <span style={{ color: 'var(--ink-mute)' }}>30d </span>
                  <Delta v={card.d30} size={12} />
                </span>
              </div>
            </div>

            <div className="modal-rhead-right">
              <div className="range-tabs">
                {(['1D', '1W', '1M', '3M', '1Y', 'ALL'] as Range[]).map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={r === range ? 'on' : ''}
                    onClick={() => setRange(r)}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="close-btn"
                onClick={() => navigate('/', { replace: true })}
                aria-label="Close"
              >
                <Icon name="close" size={12} />
              </button>
            </div>
          </div>

          <div className="modal-signal">
            <div className={`modal-signal-pill ${card.signal}`}>
              {labelForSignal(card.signal)}
              <Score score={card.score} signal={card.signal} />
              <span
                style={{
                  marginLeft: 4,
                  opacity: 0.7,
                  fontSize: 11,
                  letterSpacing: 0.04,
                }}
              >
                {card.score}/5
              </span>
            </div>
            <div className="sub-row">
              {SUBS_ORDER.map((s) => {
                // Show the actual state from `detail`. tone is applied only when
                // the sub voted (Phase 2 adversary M2: a HOLD card with mixed
                // bulls+bears must not paint all firing pills the same colour).
                const state = card.detail[s];
                const tone = state === 'bull' ? 'buy' : state === 'bear' ? 'sell' : null;
                return (
                  <span key={s} className={`sub ${tone ? `fired fired-${tone}` : ''}`}>
                    {SUB_LABELS[s]}
                    {state === 'bull' && <span style={{ marginLeft: 6, opacity: 0.7 }}>↑</span>}
                    {state === 'bear' && <span style={{ marginLeft: 6, opacity: 0.7 }}>↓</span>}
                  </span>
                );
              })}
            </div>
            {(card.burstCount ?? 0) >= 5 && (
              <div className="burst-line mono">
                🔥 burst: <strong>{card.burstCount}</strong> sales in a 5-min window
                {card.burstAtMs ? ` · peak ${fmtBurstTime(card.burstAtMs)}` : ''}
              </div>
            )}
          </div>

          <div className="trade-actions">
            <button type="button" className="btn-primary btn-buy" onClick={() => setShowBuy(true)}>
              + Buy
            </button>
            <button
              type="button"
              className="btn-primary btn-sell"
              disabled={!card.position}
              onClick={() => card.position && setShowSell(true)}
              title={card.position ? 'Close oldest open lot' : 'You have no open position'}
            >
              Sell
            </button>
          </div>

          {card.position && (
            <YourPositionBlock
              qty={card.position.qty}
              buyHkd={card.position.buyHkd}
              costHkd={card.position.costHkd}
              valueHkd={card.position.valueHkd}
              pnlHkd={card.position.pnlHkd}
              pnlPct={card.position.pnlPct}
              daysHeld={card.position.daysHeld}
              fx={fx}
            />
          )}

          <div className="modal-chart">
            <PriceChart
              history={card.history}
              collectrHistory={card.collectrHistory}
              color={sigTone}
              range={range}
              fx={fx}
            />
          </div>

          <div className="modal-foot">
            <div>
              <h4>
                Recent transactions{' '}
                <span style={{ color: 'var(--ink-mute)', marginLeft: 8 }}>
                  {card.traded.length} most recent
                </span>
              </h4>
              <table className="tx-table">
                <tbody>
                  {card.traded.slice(0, 9).map((t, i, arr) => {
                    const prev =
                      i < arr.length - 1 ? (arr[i + 1] as { price: number }).price : t.price;
                    const ch = prev === 0 ? 0 : ((t.price - prev) / prev) * 100;
                    return (
                      <tr key={`${t.t}-${t.price}-${i}`}>
                        <td>{fmtTime(t.t)}</td>
                        <td>HK${fmtHKD(usdToHkdDisplay(t.price, fx))}</td>
                        <td className={ch > 0.05 ? 'up' : ch < -0.05 ? 'down' : 'flat'}>
                          {ch > 0 ? '+' : ''}
                          {ch.toFixed(2)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div>
              <h4>Signal breakdown</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {SUBS_ORDER.map((s) => (
                  <SignalRow
                    key={s}
                    label={SUB_LABELS[s]}
                    state={card.detail[s]}
                    fired={fired.has(s)}
                    tone={sigTone}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showBuy && <BuyModal card={card} fx={fx} onClose={() => setShowBuy(false)} />}
      {showSell &&
        card.position &&
        (() => {
          // Sell the oldest open lot for this card (FIFO).
          const lot = (openHoldingsQ.data ?? [])
            .filter((h) => h.cardId === card.id && h.status === 'open')
            .sort((a, b) => a.buyAt.localeCompare(b.buyAt))[0];
          if (!lot) {
            return null;
          }
          return (
            <SellModal
              card={card}
              fx={fx}
              onClose={() => setShowSell(false)}
              holdingId={lot.id}
              buyHkd={lot.buyHkd}
              qty={lot.qty}
              daysHeld={Math.max(
                0,
                Math.floor((Date.now() - new Date(lot.buyAt).getTime()) / (24 * 60 * 60 * 1000)),
              )}
            />
          );
        })()}
    </div>
  );
}

function YourPositionBlock({
  qty,
  buyHkd,
  costHkd,
  valueHkd,
  pnlHkd,
  pnlPct,
  daysHeld,
  fx,
}: {
  qty: number;
  buyHkd: number;
  costHkd: number;
  valueHkd: number | null;
  pnlHkd: number | null;
  pnlPct: number | null;
  daysHeld: number;
  fx: FxRate;
}) {
  const pos = (pnlHkd ?? 0) >= 0;
  return (
    <div className={`your-position ${pnlHkd == null ? '' : pos ? 'pos' : 'neg'}`}>
      <div className="yp-head">
        <span className="yp-eyebrow mono">Your position</span>
        <span className="yp-meta mono">
          ×{qty} · avg HK${fmtHKD(buyHkd)} · held {daysHeld}d
        </span>
      </div>
      <div className="yp-body">
        <div className="yp-stat">
          <span className="k">Cost basis</span>
          <span className="v mono">HK${fmtHKD(costHkd)}</span>
          <span className="sub mono">≈ ${fmtUSD(costHkd / fx.usd_hkd)}</span>
        </div>
        <div className="yp-stat">
          <span className="k">Market value</span>
          <span className="v mono">{valueHkd == null ? '—' : `HK$${fmtHKD(valueHkd)}`}</span>
          <span className="sub mono">
            {valueHkd == null ? ' ' : `≈ $${fmtUSD(valueHkd / fx.usd_hkd)}`}
          </span>
        </div>
        <div className="yp-stat">
          <span className="k">Unrealized P&L</span>
          <span className={`v mono ${pnlHkd == null ? '' : pos ? 'pos' : 'neg'}`}>
            {pnlHkd == null ? '—' : fmtSignedHKD(pnlHkd)}
          </span>
          <span className="sub mono">{pnlPct == null ? ' ' : fmtPctSigned(pnlPct)}</span>
        </div>
      </div>
    </div>
  );
}

const SUB_STATE_LABELS: Record<string, string> = {
  bull: 'bullish',
  bear: 'bearish',
  neutral: 'neutral',
  insufficient: 'not enough data yet',
  disabled: 'disabled',
};

function SignalRow({
  label,
  state,
  fired,
  tone,
}: {
  label: string;
  state: 'bull' | 'bear' | 'neutral' | 'insufficient' | 'disabled';
  fired: boolean;
  tone: 'buy' | 'sell' | 'hold';
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '16px 1fr auto',
        alignItems: 'center',
        gap: 10,
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        color: fired ? 'var(--ink)' : 'var(--ink-mute)',
      }}
    >
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: 2,
          background: fired
            ? tone === 'buy'
              ? 'var(--buy)'
              : tone === 'sell'
                ? 'var(--sell)'
                : 'var(--ink-2)'
            : 'transparent',
          border: fired ? 'none' : '1px solid var(--ink-faint)',
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          style={{
            fontWeight: 500,
            fontSize: 11,
            letterSpacing: 0.03,
            textTransform: 'uppercase',
          }}
        >
          {label}
        </span>
        <span
          style={{
            color: 'var(--ink-dim)',
            fontSize: 10,
            textTransform: 'none',
            letterSpacing: 0,
          }}
        >
          {SUB_STATE_LABELS[state] ?? state}
        </span>
      </div>
      <span
        style={{
          color: fired
            ? tone === 'buy'
              ? 'var(--buy)'
              : tone === 'sell'
                ? 'var(--sell)'
                : 'var(--ink-2)'
            : 'var(--ink-mute)',
        }}
      >
        {state === 'bull' ? '↑' : state === 'bear' ? '↓' : '—'}
      </span>
    </div>
  );
}

function fmtBurstTime(ms: number): string {
  const d = new Date(ms);
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.getDate()} ${months[d.getMonth()]} ${hh}:${mm}`;
}

function fmtTime(t: string): string {
  const d = new Date(t);
  const now = new Date();
  const sameDay = d.getDate() === now.getDate() && d.getMonth() === now.getMonth();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  if (sameDay) return `${hh}:${mm}:${ss}`;
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${hh}:${mm}`;
}

// ── Cross-venue arb panel ───────────────────────────────────────────────────
// Lives in `.modal-left` because it's a static fact about ONE external listing.
// The price *history* (this venue over time) stays in `.modal-right` — that
// spatial split = "other venue" vs "this venue's history".
function ArbPanel({
  arb,
  snkrdunkUrl,
  fx,
}: {
  arb: CardArb | null;
  snkrdunkUrl: string | null;
  fx: FxRate;
}) {
  if (!arb) return null;

  // scanned, no eBay match → quiet empty panel
  if (!arb.listing || !arb.cost) {
    return (
      <div className="arb-panel is-empty">
        <div className="arb-head">
          <span className="arb-title">◇ Cross-venue · → JP</span>
          <span className="arb-tier">
            Tier {arb.tier} · {arb.scanCadence}
          </span>
        </div>
        <div className="arb-empty-body">
          <span className="em-strong">No eBay listing matched.</span>{' '}
          Scanner sweeps US · GB · DE · FR · NL · AU for verified PSA 10s.
          <span className="em-meta">
            Last sweep {arb.lastScanAt ? hoursAgo(arb.lastScanAt) : '—'} · 0 verified listings
          </span>
        </div>
      </div>
    );
  }

  const c = arb.cost;
  const l = arb.listing;
  const neg = (arb.netSpreadJpy ?? 0) < 0;
  const feeDelta = c.snkrdunkNetJpy - c.snkrdunkAskJpy; // always negative

  return (
    <div className={`arb-panel ${arb.actionable ? '' : 'is-noedge'}`}>
      <div className="arb-head">
        <span className="arb-title">
          {arb.actionable ? '◆' : '◇'} Cross-venue · → JP
        </span>
        <span className="arb-tier">
          Tier {arb.tier} · {arb.scanCadence}
        </span>
      </div>

      <div className="arb-listing">
        <div className="arb-flag">{l.country}</div>
        <div className="arb-listing-meta">
          <span className="arb-venue">
            eBay
            {l.sellerFeedbackPct != null && (
              <span className={`arb-feedback ${l.sellerFeedbackPct < 99 ? 'low' : ''}`}>
                {l.sellerFeedbackPct}%★
              </span>
            )}
          </span>
          <span className="arb-listing-sub">
            {l.sellerFeedbackScore?.toLocaleString() ?? '—'} sales · seen {hoursAgo(l.lastSeenAt)}
          </span>
        </div>
        <div className="arb-listing-price">
          {ccy(l.currency)}
          {(l.priceCents / 100).toFixed(2)}
          <small>≈ {fmtJpyAsHkd(c.ebayPriceJpy, fx)}</small>
        </div>
      </div>

      <div className="arb-stack">
        <ArbRow k="eBay price + 3% FX" v={fmtJpyAsHkd(c.ebayPriceJpy, fx)} />
        <ArbRow k={`Intl shipping (${l.country})`} v={fmtJpyAsHkd(c.shipJpy, fx)} />
        <ArbRow k="JP consumption tax" v={fmtJpyAsHkd(c.taxJpy, fx)} />
        <ArbRow k="Customs clearance" v={fmtJpyAsHkd(c.clearanceJpy, fx)} />
        <div className="arb-rule" />
        <ArbRow k="Landed cost" v={fmtJpyAsHkd(c.landedJpy, fx)} subtotal />
        <div className="arb-rule" />
        <ArbRow k="Snkrdunk ask" v={fmtJpyAsHkd(c.snkrdunkAskJpy, fx)} />
        <ArbRow k="− 9.5% fee + flats + FX out" v={fmtJpyAsHkdK(feeDelta, fx)} muted />
        <ArbRow k="Net proceeds" v={fmtJpyAsHkd(c.snkrdunkNetJpy, fx)} subtotal />
      </div>

      <div className={`arb-net ${neg ? 'neg' : ''}`}>
        <span className="arb-net-k">Net spread{neg ? ' · no edge' : ''}</span>
        <span className="arb-net-v">
          {fmtJpyAsHkdK(arb.netSpreadJpy ?? 0, fx)}
          <span className="arb-net-pct">{fmtSpreadPct(arb.netSpreadPct ?? 0, true)}</span>
        </span>
      </div>

      <div className="arb-gates">
        <ArbGate ok={arb.gates.tierA}>Tier A</ArbGate>
        <ArbGate ok={arb.gates.momentum}>
          <span className="gk">score</span> ≥3
        </ArbGate>
        <ArbGate ok={arb.gates.spread}>
          <span className="gk">spread</span> ≥15%
        </ArbGate>
        <ArbGate ok={arb.gates.band}>
          <span className="gk">¥</span> 10k–1m
        </ArbGate>
      </div>

      <div className="arb-links">
        <a
          className="arb-link"
          href={l.itemUrl}
          target="_blank"
          rel="noreferrer noopener"
        >
          eBay listing <span className="ext">↗</span>
        </a>
        {snkrdunkUrl && (
          <a
            className="arb-link"
            href={snkrdunkUrl}
            target="_blank"
            rel="noreferrer noopener"
          >
            Snkrdunk ask <span className="ext">↗</span>
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * Reverse-direction (Snkrdunk → eBay) panel — "→ US". Mirrors the import
 * ArbPanel structure for the dropship thesis: list on eBay matching the
 * cheapest comp, source from Snkrdunk when sold, ship JP→buyer, keep the
 * spread. Falls back to a ghost when arb.dropship is null (no eBay listing
 * matched in the latest scan).
 */
function DropshipPanel({ arb, fx }: { arb: CardArb; fx: FxRate }) {
  const d = arb.dropship;
  if (!d) {
    return (
      <div className="arb-panel is-empty">
        <div className="arb-head">
          <span className="arb-title">◇ Cross-venue · → US</span>
          <span className="arb-tier">Dropship · awaiting eBay comp</span>
        </div>
        <div className="arb-empty-body">
          <span className="em-strong">No eBay listing matched in the latest scan.</span>{' '}
          The dropship spread reuses the lowest verified PSA 10 comp, so this
          panel populates the moment one shows up.
        </div>
      </div>
    );
  }
  const neg = d.netSpreadJpy < 0;
  // Reuse fmtJpyAsHkd's sign-flip semantics by passing a negative number for
  // the deducted fee (always negative from the seller's perspective).
  const feeJpy = d.ebayPayoutJpy - d.ebayPriceJpy;
  return (
    <div className={`arb-panel ${neg ? 'is-noedge' : ''}`}>
      <div className="arb-head">
        <span className="arb-title">{neg ? '◇' : '◆'} Cross-venue · → US</span>
        <span className="arb-tier">Dropship · live</span>
      </div>
      <div className="arb-stack">
        <ArbRow k="eBay listing (gross)" v={fmtJpyAsHkd(d.ebayPriceJpy, fx)} />
        <ArbRow
          k={`− eBay fee (${(d.feePct * 100).toFixed(1)}%)`}
          v={fmtJpyAsHkdK(feeJpy, fx)}
          muted
        />
        <div className="arb-rule" />
        <ArbRow k="eBay payout" v={fmtJpyAsHkd(d.ebayPayoutJpy, fx)} subtotal />
        <div className="arb-rule" />
        <ArbRow k="Snkrdunk ask" v={fmtJpyAsHkd(d.snkrdunkCostJpy - d.shipJpy, fx)} />
        <ArbRow k="Snkrdunk global ship (flat)" v={fmtJpyAsHkd(d.shipJpy, fx)} />
        <ArbRow k="Snkrdunk cost (landed)" v={fmtJpyAsHkd(d.snkrdunkCostJpy, fx)} subtotal />
      </div>
      <div className={`arb-net ${neg ? 'neg' : ''}`}>
        <span className="arb-net-k">Net spread{neg ? ' · no edge' : ''}</span>
        <span className="arb-net-v">
          {fmtJpyAsHkdK(d.netSpreadJpy, fx)}
          <span className="arb-net-pct">{fmtSpreadPct(d.netSpreadPct, true)}</span>
        </span>
      </div>
    </div>
  );
}

function ArbRow({
  k,
  v,
  muted,
  subtotal,
}: {
  k: string;
  v: string;
  muted?: boolean;
  subtotal?: boolean;
}) {
  return (
    <div className={`arb-row ${muted ? 'muted' : ''} ${subtotal ? 'subtotal' : ''}`}>
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

function ArbGate({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <span className={`arb-gate ${ok ? 'pass' : ''}`}>
      {ok ? '✓' : '·'} {children}
    </span>
  );
}

const CCY_SYMBOL: Record<string, string> = {
  USD: 'US$',
  GBP: '£',
  EUR: '€',
  AUD: 'A$',
};
function ccy(c: string): string {
  return CCY_SYMBOL[c] ?? c;
}

function hoursAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const h = Math.round(ms / 3.6e6);
  if (h < 1) return '<1h ago';
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
